import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { TreePine, Mail, AlertCircle, Check, ArrowLeft } from 'lucide-react';

const ForgotPasswordPage = () => {
  const { requestPasswordReset, error, clearError } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    clearError();

    const result = await requestPasswordReset(email);
    if (result.success) {
      setSubmitted(true);
    }
    setIsLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-emerald-500/10 rounded-full flex items-center justify-center">
            <Check className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Check Your Email</h2>
          <p className="text-stone-400 mb-8">
            If an account exists with <span className="text-white">{email}</span>, you'll receive a password reset link shortly.
          </p>
          <p className="text-stone-500 text-sm mb-6">
            Note: Email sending is currently in development. Check backend logs for the reset link.
          </p>
          <Link to="/login">
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-xl">
              Return to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 group">
            <TreePine className="w-10 h-10 text-emerald-400 group-hover:scale-110 transition-transform" />
            <span className="text-2xl font-semibold text-white">
              Stay <span className="text-emerald-400">Tran-Quil</span>
            </span>
          </Link>
          <p className="text-stone-400 mt-2">Reset your password</p>
        </div>

        <div className="bg-stone-800/50 border border-stone-700/50 rounded-2xl p-8">
          <Link to="/login" className="inline-flex items-center text-stone-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to login
          </Link>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-stone-300 mb-2 block">
                Enter your email address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="pl-11 bg-stone-900/50 border-stone-600 text-white placeholder:text-stone-500 h-12"
                />
              </div>
              <p className="text-stone-500 text-sm mt-2">
                We'll send you a link to reset your password.
              </p>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-6 rounded-xl font-medium transition-all duration-300"
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
